/*
 * Pure helpers for the partner Lab test bulk upload.
 *
 * This module must stay free of React state, API calls, DOM access, and
 * message toasts so the spreadsheet contract can be unit-tested directly.
 * The pricing screen owns file reading, classification display, and drafts.
 */

export const BULK_UPLOAD_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
export const BULK_UPLOAD_MAX_DATA_ROWS = 2000;

export const BULK_TEST_FEE_MIN = 1;
export const BULK_TEST_FEE_MAX = 99999;
export const BULK_FASTING_HOURS_MIN = 1;
export const BULK_FASTING_HOURS_MAX = 23;

const REQUIRED_HEADERS = [
  "department",
  "test_group",
  "parameter_name",
  "specimen_type",
  "unit",
  "fee",
];

const HEADER_ALIAS_KEYS = {
  department: ["department", "dept"],
  test_group: ["testgroup", "group"],
  parameter_name: ["parametername", "testname", "test", "name"],
  specimen_type: ["specimenttype", "specimentype", "specimen", "sampletype", "stype"],
  unit: ["unit", "units", "uom"],
  fee: ["fee", "fees", "price", "amount", "cost"],
  description: ["description", "desc"],
  collection_rounds: ["collectionrounds", "collectionround", "rounds"],
  fasting_hours: ["fastinghours"],
  fasting_required: ["fastingrequired", "fasting"],
  prescription_required: ["prescriptionrequired", "prescription", "rxrequired"],
  discount_type: ["discounttype", "disctype", "dtype"],
  discount_value: ["discountvalue", "discvalue", "dvalue"],
  notes: ["notes", "note", "remarks"],
};

const HEADER_ALIAS_LOOKUP = Object.entries(HEADER_ALIAS_KEYS).reduce(
  (lookup, [canonical, aliases]) => {
    aliases.forEach((alias) => lookup.set(alias, canonical));
    return lookup;
  },
  new Map(),
);

const COLLECTION_ROUND_ALIASES = {
  routine: ["routine", "no fasting"],
  fasting: ["fasting"],
  post_prandial: ["post prandial", "postprandial", "pp"],
};

export const BULK_COLLECTION_ROUND_LABELS = {
  routine: "No Fasting",
  fasting: "Fasting",
  post_prandial: "Post Prandial",
};

const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

const cleanText = (value) => String(value ?? "").trim().replace(/\s+/g, " ");

const cleanNumberText = (value) =>
  String(value ?? "")
    .replace(/[\u20B9,%\s]/g, "")
    .trim();

const parseWholeNumber = (value, { min, max }) => {
  const cleaned = cleanNumberText(value);
  if (!cleaned || !/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  const number = Number(cleaned);
  if (!Number.isFinite(number) || number !== Math.trunc(number)) return null;
  if (number < min || number > max) return null;
  return number;
};

/* ---------------- Header normalization ---------------- */

export const normalizeBulkHeader = (rawHeader) => {
  const key = String(rawHeader ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (!key) return null;
  return HEADER_ALIAS_LOOKUP.get(key) || null;
};

/* ---------------- Value normalization ---------------- */

/**
 * Strict Boolean parsing for spreadsheet cells.
 * Returns true/false, or null when the value is present but unrecognized.
 */
export const normalizeBulkBoolean = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return null;
  }
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return false;
  if (["true", "yes", "y", "1"].includes(text)) return true;
  if (["false", "no", "n", "0"].includes(text)) return false;
  return null;
};

/**
 * Normalizes a human collection-round label to its canonical value.
 * Returns null for blank or unrecognized input.
 */
export const normalizeBulkCollectionRound = (value) => {
  const text = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return null;
  const match = Object.entries(COLLECTION_ROUND_ALIASES).find(([, aliases]) =>
    aliases.includes(text),
  );
  return match ? match[0] : null;
};

export const parseBulkFee = (value) =>
  parseWholeNumber(value, { min: BULK_TEST_FEE_MIN, max: BULK_TEST_FEE_MAX });

export const parseBulkFastingHours = (value) =>
  parseWholeNumber(value, { min: BULK_FASTING_HOURS_MIN, max: BULK_FASTING_HOURS_MAX });

const normalizeDiscountType = (value) => {
  const text = String(value ?? "").trim().toLowerCase();
  if (["percentage", "percent", "%"].includes(text)) return "percentage";
  if (["fixed", "rs", "rupees", "inr"].includes(text)) return "fixed";
  return null;
};

/**
 * Validates the discount_type / discount_value pair against the manual rules.
 * Returns { discount } on success (discount is null when no discount applies)
 * or { error } when the cells are invalid.
 */
export const parseBulkDiscount = (typeRaw, valueRaw, fee) => {
  const typeBlank = isBlank(typeRaw);
  const valueBlank = isBlank(valueRaw);

  if (typeBlank && valueBlank) return { discount: null };

  const type = typeBlank ? null : normalizeDiscountType(typeRaw);
  if (!typeBlank && !type) {
    return { error: "Discount type must be percentage or fixed." };
  }

  if (valueBlank) {
    return { error: "Discount value is required when discount type is supplied." };
  }

  const value = parseWholeNumber(valueRaw, { min: 0, max: Number.MAX_SAFE_INTEGER });
  if (value === null) {
    return { error: "Discount value must be a whole number." };
  }

  if (typeBlank) {
    // A bare zero value means "no discount"; anything else needs a type.
    if (value === 0) return { discount: null };
    return { error: "Discount type is required when discount value is supplied (use percentage or fixed)." };
  }

  if (value === 0) {
    return { error: "Discount value must be greater than 0." };
  }
  if (type === "percentage" && (value < 1 || value > 99)) {
    return { error: "Percentage discount must be a whole number from 1 to 99." };
  }
  if (type === "fixed") {
    if (fee !== null && fee !== undefined && value >= fee) {
      return { error: "Fixed discount must be lower than the fee." };
    }
  }
  return { discount: { type, value } };
};

/* ---------------- Identity ---------------- */

export const buildLabTestIdentity = (department, testGroup, parameterName) =>
  [
    "lab_test",
    cleanText(department).toLowerCase(),
    cleanText(testGroup).toLowerCase(),
    cleanText(parameterName).toLowerCase(),
  ].join("|");

/* ---------------- Row validation ---------------- */

const makeCellReader = (cells, columnMap) => (canonical) => {
  const index = columnMap.get(canonical);
  if (index === undefined) return "";
  const value = cells?.[index];
  return value === undefined || value === null ? "" : value;
};

/**
 * Validates one spreadsheet data row (cells already resolved by header).
 * Returns a candidate row with input flags and parse-level errors.
 */
export const validateBulkLabTestRow = (readCell, rowNumber) => {
  const errors = [];

  const department = cleanText(readCell("department"));
  const testGroup = cleanText(readCell("test_group"));
  const parameterName = cleanText(readCell("parameter_name"));
  const specimentType = cleanText(readCell("specimen_type"));
  const unit = cleanText(readCell("unit"));

  if (!department) errors.push("Department is required.");
  if (!testGroup) errors.push("Test Group is required.");
  if (!parameterName) errors.push("Parameter Name is required.");
  if (!specimentType) errors.push("Specimen Type is required.");
  if (!unit) errors.push("Unit is required.");

  const feeRaw = readCell("fee");
  const fee = isBlank(feeRaw) ? null : parseBulkFee(feeRaw);
  if (!isBlank(feeRaw) && fee === null) {
    errors.push("Fee must be a whole number from 1 to 99999.");
  }

  const descriptionRaw = readCell("description");
  const description = isBlank(descriptionRaw) ? null : cleanText(descriptionRaw);

  const notesRaw = readCell("notes");
  const notes = isBlank(notesRaw) ? null : cleanText(notesRaw);

  const prescriptionRaw = readCell("prescription_required");
  let prescriptionRequired = false;
  if (!isBlank(prescriptionRaw)) {
    const parsed = normalizeBulkBoolean(prescriptionRaw);
    if (parsed === null) {
      errors.push("Prescription Required must be yes/no, true/false, y/n, or 1/0.");
    } else {
      prescriptionRequired = parsed;
    }
  }

  const roundRaw = readCell("collection_rounds");
  const hasCollectionRoundInput = !isBlank(roundRaw);
  let collectionRoundInput = null;
  if (hasCollectionRoundInput) {
    collectionRoundInput = normalizeBulkCollectionRound(roundRaw);
    if (!collectionRoundInput) {
      errors.push("Collection Rounds must be Routine/No Fasting, Fasting, or Post Prandial (PP).");
    }
  }

  const fastingRequiredRaw = readCell("fasting_required");
  const hasFastingRequiredInput = !isBlank(fastingRequiredRaw);
  let fastingRequiredInput = null;
  if (hasFastingRequiredInput) {
    const parsed = normalizeBulkBoolean(fastingRequiredRaw);
    if (parsed === null) {
      errors.push("Fasting Required must be yes/no, true/false, y/n, or 1/0.");
    } else {
      fastingRequiredInput = parsed;
    }
  }

  if (
    collectionRoundInput &&
    fastingRequiredInput !== null &&
    fastingRequiredInput !== (collectionRoundInput === "fasting")
  ) {
    errors.push(
      "Collection Rounds and legacy Fasting Required conflict. Keep only one, or make them consistent.",
    );
  }

  const fastingHoursRaw = readCell("fasting_hours");
  const hasFastingHoursInput = !isBlank(fastingHoursRaw);
  let fastingHours = null;
  if (hasFastingHoursInput) {
    fastingHours = parseBulkFastingHours(fastingHoursRaw);
    if (fastingHours === null) {
      errors.push("Fasting Hours must be a whole number from 1 to 23.");
    }
  }

  const discountResult = parseBulkDiscount(
    readCell("discount_type"),
    readCell("discount_value"),
    fee,
  );
  let discount = null;
  const hasDiscountInput =
    !isBlank(readCell("discount_type")) || !isBlank(readCell("discount_value"));
  if (discountResult.error) {
    errors.push(discountResult.error);
  } else {
    discount = discountResult.discount;
  }

  const identity =
    department && testGroup && parameterName
      ? buildLabTestIdentity(department, testGroup, parameterName)
      : null;

  return {
    rowNumber,
    identity,
    department,
    testGroup,
    parameterName,
    specimentType,
    unit,
    fee,
    description,
    notes,
    prescriptionRequired,
    hasCollectionRoundInput,
    collectionRoundInput,
    hasFastingRequiredInput,
    fastingRequiredInput,
    hasFastingHoursInput,
    fastingHours,
    hasDiscountInput,
    discount,
    errors,
  };
};

/* ---------------- Workbook parsing ---------------- */

/**
 * Parses an array-of-arrays worksheet (first row = headers) into validated
 * candidate rows. File-level problems (headers, limits) return no rows.
 */
export const parseLabTestWorkbook = (matrix) => {
  const fileErrors = [];
  const emptyResult = { ok: false, rows: [], errors: fileErrors, totalRows: 0 };

  if (!Array.isArray(matrix) || matrix.length === 0) {
    fileErrors.push("The file does not contain a usable worksheet with data.");
    return emptyResult;
  }

  const headerIndex = matrix.findIndex(
    (row) => Array.isArray(row) && row.some((cell) => !isBlank(cell)),
  );
  if (headerIndex === -1) {
    fileErrors.push("The file does not contain a usable worksheet with data.");
    return emptyResult;
  }

  const columnMap = new Map();
  const duplicateColumns = new Set();
  matrix[headerIndex].forEach((cell, index) => {
    if (isBlank(cell)) return;
    const canonical = normalizeBulkHeader(cell);
    if (!canonical) return;
    if (columnMap.has(canonical)) duplicateColumns.add(canonical);
    else columnMap.set(canonical, index);
  });

  const missingColumns = REQUIRED_HEADERS.filter((header) => !columnMap.has(header));
  if (missingColumns.length) {
    fileErrors.push("Missing required column(s): " + missingColumns.join(", ") + ".");
  }
  if (duplicateColumns.size) {
    fileErrors.push("Duplicate column(s): " + Array.from(duplicateColumns).join(", ") + ".");
  }
  if (fileErrors.length) return emptyResult;

  const dataRows = [];
  matrix.forEach((row, index) => {
    if (index <= headerIndex) return;
    if (!Array.isArray(row) || row.every((cell) => isBlank(cell))) return;
    dataRows.push({ cells: row, rowNumber: index + 1 });
  });

  if (dataRows.length > BULK_UPLOAD_MAX_DATA_ROWS) {
    fileErrors.push(
      "The file has " + dataRows.length + " data rows, which exceeds the maximum of " +
        BULK_UPLOAD_MAX_DATA_ROWS + ". Split the file and import again.",
    );
    return { ok: false, rows: [], errors: fileErrors, totalRows: dataRows.length };
  }

  const rows = dataRows.map(({ cells, rowNumber }) => {
    const readCell = makeCellReader(cells, columnMap);
    return validateBulkLabTestRow(readCell, rowNumber);
  });

  const identityRowNumbers = new Map();
  rows.forEach((row) => {
    if (!row.identity) return;
    if (!identityRowNumbers.has(row.identity)) identityRowNumbers.set(row.identity, []);
    identityRowNumbers.get(row.identity).push(row.rowNumber);
  });
  rows.forEach((row) => {
    if (!row.identity) return;
    const rowNumbers = identityRowNumbers.get(row.identity);
    if (rowNumbers.length > 1) {
      const others = rowNumbers.filter((n) => n !== row.rowNumber).join(", ");
      row.errors.push(
        "Duplicate test in file (also in row(s) " + others + "). Remove all but one.",
      );
    }
  });

  return { ok: fileErrors.length === 0, rows, errors: fileErrors, totalRows: dataRows.length };
};

/* ---------------- Patient content ---------------- */

export const normalizeBulkPatientContent = (value) => {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const suitability =
    source.ageGenderSuitability && typeof source.ageGenderSuitability === "object"
      ? source.ageGenderSuitability
      : {};
  const cleanList = (items) =>
    (Array.isArray(items) ? items : [])
      .map((item) => String(item ?? "").trim())
      .filter(Boolean);

  return {
    basicDescription: String(source.basicDescription ?? "").trim(),
    whyBooked: cleanList(source.whyBooked),
    whoShouldTake: cleanList(source.whoShouldTake),
    ageGenderSuitability: {
      age: String(suitability.age ?? "").trim(),
      gender: String(suitability.gender ?? "").trim(),
    },
  };
};

const identityFromRowLike = (rowLike) =>
  buildLabTestIdentity(
    rowLike?.department,
    rowLike?.testGroup ?? rowLike?.test_group,
    rowLike?.parameterName ?? rowLike?.parameter_name,
  );

export const summarizeBulkLabTestClassification = (rows) =>
  (Array.isArray(rows) ? rows : []).reduce(
    (totals, row) => {
      totals.total += 1;
      if (row?.classification === "new") totals.newCount += 1;
      else if (row?.classification === "update") totals.updateCount += 1;
      else if (row?.classification === "conflict") totals.conflictCount += 1;
      else totals.invalidCount += 1;
      return totals;
    },
    { total: 0, newCount: 0, updateCount: 0, conflictCount: 0, invalidCount: 0 },
  );

/* ---------------- Classification ---------------- */

/**
 * Classifies parsed rows against saved tests, pending drafts, and the Lab
 * lookup. Adds fasting-hour cross checks that need the saved baseline.
 */
export const classifyBulkLabTestRows = (
  parsedRows,
  { savedTests = [], draftTests = [], lookupTests = [] } = {},
) => {
  const savedByIdentity = new Map(savedTests.map((test) => [identityFromRowLike(test), test]));
  const draftByIdentity = new Map(draftTests.map((test) => [identityFromRowLike(test), test]));
  const lookupByIdentity = new Map(lookupTests.map((test) => [identityFromRowLike(test), test]));

  return (Array.isArray(parsedRows) ? parsedRows : []).map((row) => {
    if (row.errors.length) {
      return {
        ...row,
        classification: "error",
        message: row.errors[0],
        patientContentSource: null,
        patientContent: null,
        baseline: null,
      };
    }

    const draftMatch = draftByIdentity.get(row.identity);
    if (draftMatch) {
      const action = draftMatch?._change === "delete" ? "removal" : "change";
      return {
        ...row,
        classification: "conflict",
        message: "A pending " + action + " already exists for this test. Remove the pending row or this spreadsheet row, then import again.",
        patientContentSource: null,
        patientContent: null,
        baseline: null,
      };
    }

    const baseline = savedByIdentity.get(row.identity) || null;
    const classification = baseline ? "update" : "new";

    // Resolve the effective collection round for this import.
    let collectionRound = row.collectionRoundInput;
    if (!collectionRound && row.hasFastingRequiredInput) {
      collectionRound = row.fastingRequiredInput ? "fasting" : "routine";
    }
    if (!collectionRound && baseline) {
      const baselineRounds = Array.isArray(baseline?.collectionRounds)
        ? baseline.collectionRounds
        : [];
      const baselineRoundKey = String(
        baselineRounds[0]?.key ?? baselineRounds[0] ?? "",
      ).trim();
      collectionRound =
        normalizeBulkCollectionRound(baselineRoundKey) ||
        (baseline?.fastingRequired ? "fasting" : null);
    }
    if (!collectionRound) collectionRound = "routine";

    // Cross-check fasting hours now that the effective round is known.
    const errors = [];
    const baselineHasValidFastingHours =
      baseline != null &&
      parseBulkFastingHours(baseline?.fastingHours ?? baseline?.fasting_hours) !== null;
    if (collectionRound === "fasting") {
      if (!row.hasFastingHoursInput && !baselineHasValidFastingHours) {
        errors.push("Fasting Hours are required (1 to 23) when the collection round is Fasting.");
      }
    } else if (row.hasFastingHoursInput) {
      errors.push("Fasting Hours must be blank unless the collection round is Fasting.");
    }

    if (errors.length) {
      return {
        ...row,
        classification: "error",
        message: errors[0],
        patientContentSource: null,
        patientContent: null,
        baseline: null,
      };
    }

    let patientContent;
    let patientContentSource;
    if (classification === "update") {
      patientContent = normalizeBulkPatientContent(baseline?.patientContent);
      patientContentSource = "preserved";
    } else {
      const lookupMatch = lookupByIdentity.get(row.identity);
      patientContent = normalizeBulkPatientContent(lookupMatch?.patientContent);
      patientContentSource = lookupMatch ? "master" : "none";
    }

    return {
      ...row,
      classification,
      message: null,
      resolvedCollectionRound: collectionRound,
      patientContentSource,
      patientContent,
      baseline,
    };
  });
};

/* ---------------- Draft building ---------------- */

/**
 * Builds the plain lab_test draft shape from a classified row. The pricing
 * screen wraps this with key/_change and normalizeRowFromLocal.
 */
export const buildBulkLabTestDraftRow = (row) => {
  if (!row || row.classification === "error" || row.classification === "conflict") {
    return null;
  }

  const baseline = row.classification === "update" ? row.baseline : null;
  const collectionRound = row.resolvedCollectionRound || row.collectionRoundInput || "routine";
  const baselineFastingHours = baseline
    ? parseBulkFastingHours(baseline?.fastingHours ?? baseline?.fasting_hours)
    : null;

  return {
    kind: "lab_test",
    department: row.department,
    testGroup: row.testGroup,
    parameterName: row.parameterName,
    specimentType: row.specimentType,
    unit: row.unit,
    fee: row.fee,
    description: row.description ?? baseline?.description ?? "",
    notes: row.notes ?? baseline?.notes ?? "",
    discount: row.discount ?? baseline?.discount ?? null,
    collectionRounds: [collectionRound],
    fastingHours: collectionRound === "fasting" ? row.fastingHours ?? baselineFastingHours : null,
    prescriptionRequired: row.prescriptionRequired,
    patientContent: row.patientContent,
  };
};
